(() => {
  if (window.__gstAnalyticsInitialized) return;
  window.__gstAnalyticsInitialized = true;

  const EVENT_PREFIX = 'gstbody:';
  const pendingEvents = [];
  let flushTimer = null;

  const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, 100);

  const deviceContext = () => {
    if (window.matchMedia('(max-width: 760px)').matches) return 'mobile';
    if (window.matchMedia('(max-width: 1050px)').matches) return 'tablet';
    return 'desktop';
  };

  const destinationType = (link) => {
    const href = link?.getAttribute('href') || '';
    if (href.startsWith('mailto:')) return 'email';
    if (href.startsWith('tel:')) return 'phone';
    if (href.startsWith('#')) return 'page_anchor';

    try {
      return new URL(href, window.location.href).origin === window.location.origin ? 'internal' : 'external';
    } catch (_error) {
      return 'unknown';
    }
  };

  const destination = (link) => {
    const type = destinationType(link);
    if (type === 'email' || type === 'phone') return type;

    try {
      const url = new URL(link.href, window.location.href);
      return type === 'external' ? `${url.hostname}${url.pathname}` : `${url.pathname}${url.hash}`;
    } catch (_error) {
      return '';
    }
  };

  const analyticsReady = () => typeof window.Shopify?.analytics?.publish === 'function';

  const analyticsAllowed = () => {
    const permissionCheck = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed;
    return typeof permissionCheck !== 'function' || permissionCheck.call(window.Shopify.customerPrivacy);
  };

  const flush = () => {
    if (!analyticsReady()) return;
    if (!analyticsAllowed()) {
      pendingEvents.length = 0;
      return;
    }
    while (pendingEvents.length) {
      const event = pendingEvents.shift();
      Promise.resolve(
        window.Shopify.analytics.publish(`${EVENT_PREFIX}${event.name}`, event.payload)
      ).catch(() => {});
    }
    if (flushTimer) window.clearInterval(flushTimer);
    flushTimer = null;
  };

  const publish = (name, payload = {}) => {
    pendingEvents.push({
      name,
      payload: {
        ...payload,
        device_context: deviceContext(),
        page_path: window.location.pathname
      }
    });
    flush();

    if (!flushTimer) {
      let attempts = 0;
      flushTimer = window.setInterval(() => {
        attempts += 1;
        flush();
        if (attempts >= 40 && flushTimer) {
          window.clearInterval(flushTimer);
          flushTimer = null;
          pendingEvents.length = 0;
        }
      }, 250);
    }
  };

  const productPayload = (card, link) => ({
    item_id: card.dataset.productId || card.dataset.productHandle || '',
    item_name: card.dataset.productTitle || cleanText(card.querySelector('h2, h3, h4')?.textContent),
    item_handle: card.dataset.productHandle || link.pathname.split('/products/')[1]?.split(/[?#]/)[0] || '',
    price: Number(card.dataset.productPrice || 0) / 100,
    currency: card.dataset.currency || 'USD',
    item_list_name: card.dataset.listName || 'Shop',
    destination: destination(link)
  });

  const navigationPayload = (link) => {
    const isMobile = Boolean(link.closest('[data-gst-mobile-nav]'));
    const desktopGroup = link.closest('[data-gst-nav-group]');
    const mobileGroup = link.closest('details');
    const groupLabel = isMobile
      ? cleanText(mobileGroup?.querySelector(':scope > summary')?.textContent)
      : cleanText(desktopGroup?.querySelector(':scope > button')?.textContent);

    return {
      menu: isMobile ? 'mobile_header' : 'desktop_header',
      group: groupLabel || 'primary',
      label: cleanText(link.textContent),
      destination: destination(link),
      destination_type: destinationType(link)
    };
  };

  const trackLink = (link) => {
    const header = link.closest('[data-gst-navigation]');
    if (header && !link.matches('[data-gst-cart-trigger]')) {
      publish('navigation_select', navigationPayload(link));
      return;
    }

    const shop = link.closest('.gst-shop');
    if (!shop && !(link.closest('main') && cleanText(link.textContent))) return;

    const productCard = link.closest('[data-gst-product-card]');
    if (productCard) {
      const eventName = productCard.dataset.listName === 'Sale items' ? 'sale_item_select' : 'product_select';
      publish(eventName, productPayload(productCard, link));
      return;
    }

    if (link.closest('.gst-shop__catalog-nav')) {
      publish('shop_filter_select', {
        function: cleanText(link.textContent),
        anchor: link.hash.replace('#', '')
      });
      return;
    }

    if (link.closest('.gst-shop__guide-grid')) {
      const recommendation = link.closest('article');
      publish('equipment_guide_select', {
        recommendation: cleanText(recommendation?.querySelector('h3')?.textContent),
        product_handle: link.pathname.split('/products/')[1]?.split(/[?#]/)[0] || '',
        label: cleanText(link.textContent),
        destination: destination(link)
      });
      return;
    }

    if (link.getAttribute('href')?.startsWith('mailto:')) {
      publish('contact_intent', {
        label: cleanText(link.textContent),
        placement: link.closest('#gst-shop-help') ? 'equipment_help' : (shop ? 'shop_page' : (window.location.pathname === '/' ? 'home_page' : 'content_page'))
      });
      return;
    }

    publish('cta_select', {
      cta_label: cleanText(link.textContent),
      section_id: link.closest('section')?.querySelector('[id]')?.id || link.closest('section')?.id || link.closest('section')?.className.split(' ')[0] || 'shop_navigation',
      destination: destination(link),
      destination_type: destinationType(link)
    });
  };

  const bindClicks = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;

      if (link.matches('[data-gst-cart-trigger]')) {
        publish('cart_trigger_select', {
          item_count: Number(cleanText(link.querySelector('.gst-cart-count')?.textContent) || 0),
          placement: 'header'
        });
        return;
      }

      trackLink(link);
    }, { capture: true });
  };

  const bindCartDrawer = () => {
    const drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    let wasOpen = drawer.classList.contains('drawer--is-open');
    const observer = new MutationObserver(() => {
      const isOpen = drawer.classList.contains('drawer--is-open');
      if (isOpen && !wasOpen) {
        fetch(`${window.theme?.routes?.cart || '/cart.js'}`, { credentials: 'same-origin' })
          .then((response) => response.json())
          .then((cart) => {
            publish('cart_drawer_view', {
              item_count: Number(cart.item_count || 0),
              cart_value: Number(cart.total_price || 0) / 100,
              currency: cart.currency || 'USD',
              items: (cart.items || []).map((item) => ({
                item_id: String(item.product_id || item.id || ''),
                item_variant_id: String(item.variant_id || ''),
                item_name: item.product_title || item.title || '',
                item_handle: item.handle || '',
                price: Number(item.final_price || item.price || 0) / 100,
                quantity: Number(item.quantity || 0)
              }))
            });
          })
          .catch(() => publish('cart_drawer_view'));
      }
      wasOpen = isOpen;
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };

  const initialize = () => {
    bindClicks();
    bindCartDrawer();
    flush();
    document.addEventListener('shopifyCustomerPrivacyApiLoaded', flush);
    document.addEventListener('visitorConsentCollected', flush);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
